"use client";

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import { Calendar } from "lucide-react";
import { useFetchNewsArticles, useFetchNewsArticlesCount } from "@/apis/queryFunctions/news";
import { dictionaries, Locale } from "@/utils/i18n";

interface TinTucClientProps {
  homepageData: any;
  locale: string;
}

const formatDate = (dateString: string) => {
  if (!dateString) return "";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (e) {
    return dateString;
  }
};

export default function TinTucClient({ locale }: TinTucClientProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9;

  // Fetch live articles list from query
  const { data: newsItems = [], isLoading } = useFetchNewsArticles({
    page: currentPage,
    limit: pageSize,
    condition: { ngon_ngu: locale },
  });

  // Fetch total count for pagination
  const { data: totalItems = 0 } = useFetchNewsArticlesCount({
    condition: { ngon_ngu: locale },
  });

  const totalPages = Math.ceil(totalItems / pageSize);

  const dict =
    dictionaries[locale as Locale]?.news_flow || dictionaries.vi.news_flow;

  return (
    <main className="min-h-screen bg-[#06191e] pt-[80px] md:pt-[150px]">
      {/* Banner Section */}
      <section className="relative py-20 md:py-28 text-white text-center overflow-hidden border-b border-white/5">
        {/* Subtle slow glowing background effect */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at center, #0e3d48 0%, #06191e 100%)",
          }}
        />
        {/* Decorative pattern lines */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-6 space-y-4">
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-wider text-[#DBB653] font-montserrat">
            {dict.blog_title}
          </h1>
          <div className="w-[80px] h-[2px] bg-gradient-to-r from-transparent via-[#DBB653] to-transparent mx-auto my-3" />
          <p className="text-xs sm:text-sm md:text-base text-white/70 font-medium max-w-2xl mx-auto font-montserrat leading-relaxed">
            {dict.blog_desc}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 max-w-7xl mx-auto px-6 md:px-8">
        {isLoading ? (
          <div className="flex justify-center items-center py-24">
            <div className="w-12 h-12 border-4 border-[#DBB653] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : newsItems.length === 0 ? (
          <div className="text-center py-16 text-white/60 font-semibold text-sm">
            {dict.no_results}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {newsItems.map((item: any) => {
              const imageUrl = appendImageUrlFromAPI({
                src: item.picture || item.image,
                size: "xl",
              });
              return (
                <Link
                  key={item._id || item.id}
                  href={`/${locale}/tin-tuc/${item.slug}`}
                  className="flex flex-col bg-white/5 rounded-2xl overflow-hidden border border-white/10 hover:border-[#DBB653]/30 hover:shadow-[0_8px_40px_rgba(219,182,83,0.15)] transition-all duration-300 cursor-pointer group h-full"
                >
                  {/* Cover image */}
                  <div className="relative aspect-[16/10] bg-white/5 overflow-hidden">
                    <Image
                      src={imageUrl || "/images/profession1.png"}
                      alt={item.title}
                      fill
                      sizes="(min-width: 1024px) 30vw, (min-width: 768px) 50vw, 100vw"
                      className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>

                  {/* Metadata Content */}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-1.5 text-white/40 text-[11px] font-bold mb-3 uppercase tracking-wider">
                      <Calendar size={12} className="text-[#DBB653]" />
                      <span>{formatDate(item.date_created || item.date)}</span>
                    </div>
                    <h3 className="text-sm sm:text-base font-extrabold text-white group-hover:text-[#DBB653] transition duration-200 line-clamp-2 leading-snug mb-3 font-montserrat">
                      {item.title}
                    </h3>
                    <p className="text-xs text-white/60 line-clamp-3 leading-relaxed flex-1 font-montserrat">
                      {item.summary ||
                        (item.content
                          ? item.content
                              .replace(/<[^>]*>/g, "")
                              .substring(0, 120) + "..."
                          : "")}
                    </p>
                    <span className="text-[#DBB653] font-bold text-xs uppercase tracking-wider mt-4 inline-block group-hover:translate-x-1.5 transition-transform duration-300">
                      {dict.read_more || "Đọc chi tiết"} →
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Pagination Controls */}
        {!isLoading && totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-14">
            <button
              onClick={() => {
                setCurrentPage((prev) => {
                  const next = Math.max(1, prev - 1);
                  window.scrollTo({ top: 300, behavior: "smooth" });
                  return next;
                });
              }}
              disabled={currentPage === 1}
              className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-[#DBB653] disabled:opacity-30 disabled:hover:border-white/10 disabled:cursor-not-allowed transition duration-200"
            >
              &larr;
            </button>
            
            {Array.from({ length: totalPages }).map((_, idx) => {
              const pageNum = idx + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => {
                    setCurrentPage(pageNum);
                    window.scrollTo({ top: 300, behavior: "smooth" });
                  }}
                  className={`w-10 h-10 rounded-full border flex items-center justify-center font-bold text-xs transition duration-200 ${
                    currentPage === pageNum
                      ? "bg-[#DBB653] text-[#06191e] border-[#DBB653] shadow-[0_0_15px_rgba(219,182,83,0.3)]"
                      : "border-white/10 text-white/70 hover:text-white hover:border-white/30"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => {
                setCurrentPage((prev) => {
                  const next = Math.min(totalPages, prev + 1);
                  window.scrollTo({ top: 300, behavior: "smooth" });
                  return next;
                });
              }}
              disabled={currentPage === totalPages}
              className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-[#DBB653] disabled:opacity-30 disabled:hover:border-white/10 disabled:cursor-not-allowed transition duration-200"
            >
              &rarr;
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
